import styled, { css } from 'styled-components';
import { Box, Flex, font } from '@lemonade-hq/cdk';
import { MEDIA_QUERIES } from '@lemonade-hq/nuis';
import { COLORS } from '@lemonade-hq/tokens';
import { Section, SectionTitle, SectionParagraph } from '@/components/shared/Content';
import stepOneImg from '@/static/assets/notion/step-one.svg';
import stepTwoImg from '@/static/assets/notion/step-two.svg';
import stepThreeImg from '@/static/assets/notion/step-three.svg';
import waveImg from '@/static/assets/notion/wave.png';
import arrow from '@/static/assets/notion/arrow.svg';

const ContentWrapper = styled(Box)`
  max-width: 1014px;

  ${MEDIA_QUERIES.desktop`
        max-width: 1044px;
    `}
`;

const horizontalArrow = css`
  background: transparent url(${arrow.src}) no-repeat;

  ${MEDIA_QUERIES.desktop`
        right: -32px;
        top: 83px;
        width: 15px;
        height: 30px;
        left: unset;
    `}
`;

const WaveImg = styled.img`
  height: 206px;
  width: 100%;
  background-color: ${COLORS.white};

  ${MEDIA_QUERIES.mobile`
        width: 250%;
        height: auto;
    `}
`;

const SubtitleContainer = styled(Box)`
  width: 100%;
  max-width: 990px;

  ${MEDIA_QUERIES.mobile`
        padding: 20px 48px 0 48px;
        margin-top: 0;
    `}
`;

const Subtitle = styled(SectionParagraph)`
  ${MEDIA_QUERIES.tabletAndDesktop`
        white-space: pre;
    `}

  ${MEDIA_QUERIES.mobile`
        font-size: 2rem;
    `}
`;

const StepParagraph = styled(SectionParagraph)`
  font-size: 1.6rem;
  line-height: 21px;
`;

const DisclaimerContainer = styled(Box)`
  width: 100%;
  max-width: 1100px;
`;

const Disclaimer = styled.p`
  white-space: initial;
  text-align: center;
  color: ${COLORS.metal};
  ${font('main', { fontSize: '1.6rem', lineHeight: '2rem' })};

  ${MEDIA_QUERIES.mobile`
        font-size: 1.4rem;
    `}
`;

const DisclaimerTitle = styled(Disclaimer)`
  padding-bottom: 15px;
  font-weight: bold;
  color: ${COLORS.night};
`;

const HowItWorksStep = styled(Flex)`
  width: 300px;
  position: relative;

  &:before {
    height: 200px;
    margin-bottom: 24px;
  }

  &:after {
    flex: 1;
    width: 15px;
    height: 30px;
    bottom: -60px;

    ${MEDIA_QUERIES.desktop`
            ${horizontalArrow}
        `};

    background-size: contain;
  }

  ${MEDIA_QUERIES.upToDesktop`
        width: 200px;
    `}

  ${MEDIA_QUERIES.desktop`
        height: 370px;

    `}
`;

const NewPolicyStep = styled(HowItWorksStep)`
  &:before {
    content: '';
    display: block;
    background: transparent url(${stepOneImg.src}) no-repeat;
    background-size: contain;
    width: 199px;
    height: 200px;
  }

  &:after {
    content: '';
    position: absolute;
    background-size: contain;
  }

  ${MEDIA_QUERIES.desktop`
        &:before {
            background: transparent url(${stepOneImg.src}) no-repeat bottom;
            background-size: contain;
        }
    `}
`;

const RedeemCodeStep = styled(HowItWorksStep)`
  &:before {
    content: '';
    display: block;
    background: transparent url(${stepTwoImg.src}) no-repeat;
    background-size: contain;
    width: 204px;
    height: 199px;
  }

  &:after {
    content: '';
    position: absolute;
    background-size: contain;
  }

  ${MEDIA_QUERIES.desktop`
        &:before {
            height: 199px;
            background: transparent url(${stepTwoImg.src}) no-repeat bottom;
            background-size: contain;
        }
    `}
`;

const DeviceStep = styled(HowItWorksStep)`
  &:before {
    content: '';
    display: block;
    background: ${`transparent url(${stepThreeImg.src}) no-repeat`};
    background-size: contain;
    width: 204px;
    height: 199px;
    margin-bottom: 24px;
  }

  ${MEDIA_QUERIES.desktop`
        &:before {
            height: 199px;
            background: ${`transparent url(${stepThreeImg.src}) no-repeat center`};
            background-size: contain;
            margin-bottom: 24px;
        }
    `}
`;

export interface HowNotionWorksProps {
  title: string;
  subtitle: string;
  steps: {
    first: string;
    second: string;
    third: string;
  };
  disclaimer: {
    title: string;
    paragraph: string;
  };
}

export const HowNotionWorks = ({ title, subtitle, steps, disclaimer }: HowNotionWorksProps) => {
  return (
    <Section darkBackground mt={[-3, -3, -1]} pt={[55, 55, 0]}>
      <SectionTitle>{title}</SectionTitle>
      <SubtitleContainer mt={[25, 19]} px={20}>
        <Subtitle>{subtitle}</Subtitle>
      </SubtitleContainer>
      <ContentWrapper>
        <Flex flexDirection={['column', 'column', 'row']} alignItems={['center', 'center']} mt={[60, 0]}>
          <NewPolicyStep flexDirection="column" alignItems="center" mt={[0, 100]} mb={[40, 57, 0]}>
            <StepParagraph>{steps.first}</StepParagraph>
          </NewPolicyStep>
          <RedeemCodeStep flexDirection="column" alignItems="center" mt={[0, 30, 100]} mb={[40, 57, 0]} ml={[0, 0, 51]}>
            <StepParagraph>{steps.second}</StepParagraph>
          </RedeemCodeStep>
          <DeviceStep
            flexDirection="column"
            alignItems="center"
            mt={[0, 30, 100]}
            mb={[0, 30, 0]}
            ml={[0, 0, 51]}
            img={stepThreeImg}
          >
            <StepParagraph>{steps.third}</StepParagraph>
          </DeviceStep>
        </Flex>
        <DisclaimerContainer mt={60} px={70}>
          <DisclaimerTitle>{disclaimer.title}</DisclaimerTitle>
          <Disclaimer>{disclaimer.paragraph}</Disclaimer>
        </DisclaimerContainer>
      </ContentWrapper>
      <WaveImg src={waveImg.src} />
    </Section>
  );
};
